import { isEqual, merge, mergeWith } from "lodash";
import { APP_USER_ID_KEY } from "src/constants/app";
import { create } from "zustand";

const DEFAULT_SESSION_PREFERENCES = {
  tables: {},
  cards: {},
  charts: {},
  filters: {
    main: {
      dateFrom: "",
      dateTo: "",
      type: "",
      clientProvider: [],
      status: [],
      account: "",
      paymentMethod: [],
      tags: [],
      category: [],
      year: "",
    },
  },
  quickCalculations: {
    includeIncome: true,
    includeExpenses: true,
    type: "sum",
  },
};

const DEFAULT_TABLE_PREFERENCES = {
  perPage: 10,
  quickFilter: "",
  currentPage: 0,
};

const DEFAULT_CHART_PREFERENCES = {
  zoom: {
    start: 0,
    end: 100,
  },
};

const cleanSessionPreferences = (state: any) => ({
  tables: state.tables,
  cards: state.cards,
  charts: state.charts,
  filters: state.filters,
  quickCalculations: state.quickCalculations,
});

const getSessionPreferencesStorageKey = (userId: string) => {
  return `${userId}-temporal`;
};

const getStoredSessionPreferences = () => {
  if (typeof window === "undefined") {
    return DEFAULT_SESSION_PREFERENCES;
  }

  try {
    const userId = localStorage.getItem(APP_USER_ID_KEY);

    if (!userId) {
      return DEFAULT_SESSION_PREFERENCES;
    }

    const raw = sessionStorage.getItem(getSessionPreferencesStorageKey(userId));

    if (!raw) {
      return DEFAULT_SESSION_PREFERENCES;
    }

    const parsed = JSON.parse(raw);

    return merge({}, DEFAULT_SESSION_PREFERENCES, parsed);
  } catch (error) {
    console.error(
      "Error loading session preferences from sessionStorage",
      error,
    );
    return DEFAULT_SESSION_PREFERENCES;
  }
};

const saveSessionPreferences = (state: any) => {
  if (typeof window === "undefined") return;

  try {
    const userId = localStorage.getItem(APP_USER_ID_KEY);

    if (!userId) return;

    sessionStorage.setItem(
      getSessionPreferencesStorageKey(userId),
      JSON.stringify(cleanSessionPreferences(state)),
    );
  } catch (error) {
    console.error("Error saving session preferences to sessionStorage", error);
  }
};

const removeSessionPreferences = () => {
  if (typeof window === "undefined") return;

  try {
    const userId = localStorage.getItem(APP_USER_ID_KEY);

    if (!userId) return;

    sessionStorage.removeItem(getSessionPreferencesStorageKey(userId));
  } catch (error) {
    console.error(
      "Error removing session preferences from sessionStorage",
      error,
    );
  }
};

const mergeSessionPreferences = (currentData: any, updates: any) => {
  return mergeWith({}, currentData, updates, (objValue, srcValue) => {
    if (srcValue === undefined) {
      return objValue;
    }

    if (srcValue === "") {
      return "";
    }

    if (srcValue === null) {
      return null;
    }

    if (Array.isArray(srcValue)) {
      return srcValue;
    }

    return undefined;
  });
};

const useSessionPreferences = create((set: any, get: any) => ({
  ...getStoredSessionPreferences(),

  loadSessionPreferences: () => {
    const storedPreferences = getStoredSessionPreferences();
    set(storedPreferences);
  },

  resetSessionPreferences: () => {
    removeSessionPreferences();
    set({ ...DEFAULT_SESSION_PREFERENCES });
  },

  setSessionPreferences: (updates: any) => {
    const state = get();
    const currentData = cleanSessionPreferences(state);

    const newState = mergeSessionPreferences(currentData, updates);

    if (!isEqual(currentData, newState)) {
      set(newState);
      saveSessionPreferences(newState);
    }
  },

  getTablePreferences: (tableId: string) => {
    const state = get();
    return merge({}, DEFAULT_TABLE_PREFERENCES, state.tables?.[tableId] || {});
  },

  setTablePreferences: (tableId: string, updates: any) => {
    const state = get();
    const currentTablePreferences = merge(
      {},
      DEFAULT_TABLE_PREFERENCES,
      state.tables?.[tableId] || {},
    );

    const newTablePreferences = mergeSessionPreferences(
      currentTablePreferences,
      updates,
    );

    if (isEqual(currentTablePreferences, newTablePreferences)) {
      return;
    }

    const newTables = {
      ...state.tables,
      [tableId]: newTablePreferences,
    };

    const newState = {
      ...cleanSessionPreferences(state),
      tables: newTables,
    };

    set({ tables: newTables });
    saveSessionPreferences(newState);
  },

  resetTablePreferences: (tableId: string) => {
    const state = get();

    if (!state.tables?.[tableId]) {
      return;
    }

    const newTables = { ...state.tables };
    delete newTables[tableId];

    const newState = {
      ...cleanSessionPreferences(state),
      tables: newTables,
    };

    set({ tables: newTables });
    saveSessionPreferences(newState);
  },

  resetAllTablePreferences: () => {
    const state = get();

    const newState = {
      ...cleanSessionPreferences(state),
      tables: {},
    };

    set({ tables: {} });
    saveSessionPreferences(newState);
  },

  getChartPreferences: (chartId: string) => {
    const state = get();
    return merge({}, DEFAULT_CHART_PREFERENCES, state.charts?.[chartId] || {});
  },

  setChartPreferences: (chartId: string, updates: any) => {
    const state = get();
    const currentChartPreferences = merge(
      {},
      DEFAULT_CHART_PREFERENCES,
      state.charts?.[chartId] || {},
    );

    const newChartPreferences = mergeSessionPreferences(
      currentChartPreferences,
      updates,
    );

    if (isEqual(currentChartPreferences, newChartPreferences)) {
      return;
    }

    const newCharts = {
      ...state.charts,
      [chartId]: newChartPreferences,
    };

    const newState = {
      ...cleanSessionPreferences(state),
      charts: newCharts,
    };

    set({ charts: newCharts });
    saveSessionPreferences(newState);
  },

  resetChartPreferences: (chartId: string) => {
    const state = get();

    if (!state.charts?.[chartId]) {
      return;
    }

    const newCharts = { ...state.charts };
    delete newCharts[chartId];

    const newState = {
      ...cleanSessionPreferences(state),
      charts: newCharts,
    };

    set({ charts: newCharts });
    saveSessionPreferences(newState);
  },

  resetAllChartPreferences: () => {
    const state = get();

    const newState = {
      ...cleanSessionPreferences(state),
      charts: {},
    };

    set({ charts: {} });
    saveSessionPreferences(newState);
  },
}));

export default useSessionPreferences;
